/**
 * Retro sound effects for the hidden Space Invaders game (C).
 * Everything is synthesised with the Web Audio API — square/sawtooth blips,
 * noise bursts and the famous four-note march — so there are no audio assets.
 *
 * Browsers only let audio start after a user gesture, so the component calls
 * unlock() from the Start button / key press before any play(). The engine
 * emits plain events (state.events) and the component forwards them here.
 *
 * createSound() returns an isolated instance: no module-level mutable state.
 */

// A2 G2 F2 E2 — the descending bass "thump" that speeds up with the invaders.
export const MARCH_FREQS = [110, 98, 87.31, 82.41];

const SIREN_BASE_HZ = 600;
const SIREN_WOBBLE_HZ = 6;
const SIREN_DEPTH_HZ = 150;
const SILENT = 0.0001;

function defaultAudioContextFactory() {
    if (typeof window === 'undefined') {
        return null;
    }
    const Ctor = window.AudioContext || window.webkitAudioContext;
    return Ctor ? new Ctor() : null;
}

export function createSound({ audioContextFactory = defaultAudioContextFactory, muted = false } = {}) {
    let ctx = null;
    let triedFactory = false;
    let isMuted = !!muted;
    let disposed = false;
    let siren = null;

    const now = () => (ctx && typeof ctx.currentTime === 'number' ? ctx.currentTime : 0);

    function envelope(gainValue, start, duration) {
        const g = ctx.createGain();
        g.gain.setValueAtTime(gainValue, start);
        g.gain.exponentialRampToValueAtTime(SILENT, start + duration);
        g.connect(ctx.destination);
        return g;
    }

    function tone({
        freq, endFreq = null, type = 'square', duration = 0.1, gain = 0.15, at = 0,
    }) {
        const start = now() + at;
        const osc = ctx.createOscillator();
        osc.type = type;
        osc.frequency.setValueAtTime(freq, start);
        if (endFreq) {
            osc.frequency.exponentialRampToValueAtTime(endFreq, start + duration);
        }
        osc.connect(envelope(gain, start, duration));
        osc.start(start);
        osc.stop(start + duration + 0.02);
    }

    function noise(duration, gain) {
        const length = Math.max(1, Math.floor(ctx.sampleRate * duration));
        const buffer = ctx.createBuffer(1, length, ctx.sampleRate);
        const data = buffer.getChannelData(0);
        for (let i = 0; i < length; i += 1) {
            data[i] = Math.random() * 2 - 1;
        }
        const start = now();
        const src = ctx.createBufferSource();
        src.buffer = buffer;
        src.connect(envelope(gain, start, duration));
        src.start(start);
        src.stop(start + duration);
    }

    function startSiren() {
        if (siren) {
            return;
        }
        const start = now();
        const osc = ctx.createOscillator();
        osc.type = 'triangle';
        osc.frequency.setValueAtTime(SIREN_BASE_HZ, start);
        const lfo = ctx.createOscillator();
        lfo.type = 'sine';
        lfo.frequency.setValueAtTime(SIREN_WOBBLE_HZ, start);
        const depth = ctx.createGain();
        depth.gain.setValueAtTime(SIREN_DEPTH_HZ, start);
        lfo.connect(depth);
        depth.connect(osc.frequency);
        const out = ctx.createGain();
        out.gain.setValueAtTime(0.08, start);
        osc.connect(out);
        out.connect(ctx.destination);
        osc.start(start);
        lfo.start(start);
        siren = { osc, lfo };
    }

    function stopSiren() {
        if (!siren) {
            return;
        }
        const stopAt = now();
        siren.osc.stop(stopAt);
        siren.lfo.stop(stopAt);
        siren = null;
    }

    const effects = {
        march: event => tone({
            freq: MARCH_FREQS[(event.note || 0) % MARCH_FREQS.length], type: 'square', duration: 0.09, gain: 0.25,
        }),
        shoot: () => tone({
            freq: 880, endFreq: 220, type: 'square', duration: 0.12, gain: 0.12,
        }),
        invaderHit: () => noise(0.12, 0.25),
        playerHit: () => {
            stopSiren();
            noise(0.5, 0.3);
            tone({
                freq: 300, endFreq: 40, type: 'sawtooth', duration: 0.5, gain: 0.2,
            });
        },
        ufoSpawn: () => startSiren(),
        ufoGone: () => stopSiren(),
        ufoHit: () => {
            stopSiren();
            [440, 660, 880].forEach((freq, i) => tone({
                freq, type: 'square', duration: 0.09, gain: 0.15, at: i * 0.08,
            }));
        },
        waveCleared: () => {
            stopSiren();
            [523.25, 659.25, 783.99, 1046.5].forEach((freq, i) => tone({
                freq, type: 'square', duration: 0.12, gain: 0.15, at: i * 0.1,
            }));
        },
        gameOver: () => {
            stopSiren();
            [330, 262, 196].forEach((freq, i) => tone({
                freq, type: 'sawtooth', duration: 0.22, gain: 0.18, at: i * 0.2,
            }));
        },
    };

    return {
        unlock() {
            if (disposed) {
                return;
            }
            if (!ctx && !triedFactory) {
                triedFactory = true;
                ctx = audioContextFactory() || null;
            }
            if (ctx && ctx.state !== 'running' && typeof ctx.resume === 'function') {
                const resumed = ctx.resume();
                if (resumed && typeof resumed.catch === 'function') {
                    resumed.catch(() => {});
                }
            }
        },

        play(event) {
            if (!ctx || disposed || isMuted || !event) {
                return;
            }
            const effect = effects[event.type];
            if (effect) {
                effect(event);
            }
        },

        setMuted(value) {
            isMuted = !!value;
            if (isMuted && ctx) {
                stopSiren();
            }
        },

        isMuted() {
            return isMuted;
        },

        dispose() {
            if (ctx) {
                stopSiren();
                if (typeof ctx.close === 'function') {
                    const closed = ctx.close();
                    if (closed && typeof closed.catch === 'function') {
                        closed.catch(() => {});
                    }
                }
            }
            disposed = true;
        },
    };
}
